<template>
  <div>
    <canvas class="dots-canvas" ref="dots-canvas" />
    <controls
      :initial-color-ratio="grid.colorRatio"
      :draw-mode="grid.drawMode"
      :theme="theme"
      @set-draw-mode-color="grid.drawMode.color = $event"
      @toggle="toggleDrawMode()"
      @update-color-ratio="updateColorRatio($event)"
    />
  </div>
</template>

<script>
import controls from "./Controls.vue";

export default {
  components: {
    controls,
  },
  props: {
    theme: {
      type: Object,
      default: () => {
        return {
          colors: [
            "#E06C75",
            "#98C379",
            "#E5C07B",
            "#61AFEF",
            "#C678DD",
            "#56B6C2",
          ],
          gray: "#424750",
        };
      },
    },
  },
  data() {
    return {
      grid: {
        canvas: null,
        context: null,
        offscreenCanvas: null,
        offscreenContext: null,
        colorRatio: 3,
        dotSpacing: 32,
        width: null,
        height: null,
        dotRadius: 2,
        maxDotRadius: 5,
        allDots: {
          "#424750": {},
          "#E06C75": {},
          "#98C379": {},
          "#E5C07B": {},
          "#61AFEF": {},
          "#C678DD": {},
          "#56B6C2": {},
        },
        animations: {
          ripple: {
            duration: 1000,
            dotSpeed: 250,
            origin: {
              x: window.scrollX + window.innerWidth / 2,
              y: window.scrollY + window.innerHeight / 2,
            },
          },
          popColor: {
            duration: 2000,
            dotSpeed: 250,
          },
        },
        drawMode: {
          active: false,
          color: "random",
          isDrawing: false,
        },
      },
      screenWidth: null,
    };
  },
  created() {
    window.addEventListener("resize", this.debounce(this.resizeHandler));
  },
  destroyed() {
    window.removeEventListener("resize", this.debounce(this.resizeHandler));
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      //Initialize canvas object used for drawing
      let body = document.body;

      this.screenWidth = body.clientWidth;

      if (!this.grid.canvas) {
        this.grid.canvas = await this.$refs["dots-canvas"];
      }
      this.grid.canvas.width = body.clientWidth;
      this.grid.canvas.height = Math.max(body.clientHeight, body.scrollHeight);
      this.grid.context = this.grid.canvas.getContext("2d");

      //Calculate grid width based on spacing of dots.
      this.grid.width = Math.ceil(
        this.grid.canvas.width / this.grid.dotSpacing
      );

      this.grid.height = Math.ceil(
        this.grid.canvas.height / this.grid.dotSpacing
      );

      this.grid.animations.ripple.origin.x =
        window.scrollX + window.innerWidth / 2;
      this.grid.animations.ripple.origin.y =
        window.scrollY + window.innerHeight / 2;

      //Initialize dot data
      //Resets all dots for when init is called again
      let keys = Object.keys(this.grid.allDots);
      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        this.grid.allDots[key] = {};
      }
      let start = Date.now();
      for (let i = 0; i < this.grid.height; i++) {
        for (let j = 0; j < this.grid.width; j++) {
          let dot = {
            x: this.grid.dotSpacing * j,
            y: this.grid.dotSpacing * i,
            radius: this.grid.dotRadius,
            animation: {
              type: "ripple",
              startTime: start,
            },
          };
          this.grid.allDots[this.theme.gray][dot.x] =
            this.grid.allDots[this.theme.gray][dot.x] || {};
          this.grid.allDots[this.theme.gray][dot.x][dot.y] = dot;
        }
      }
      this.drawAllDots();
    },
    drawAllDots() {
      //Clears visible canvas
      this.grid.context.clearRect(
        0,
        0,
        this.grid.canvas.width,
        this.grid.canvas.height
      );

      //Draw remaining active dots by color
      var animationIsActive = false;

      var colorGroups = Object.entries(this.grid.allDots);
      for (let groupIdx = 0; groupIdx < colorGroups.length; groupIdx++) {
        var color = colorGroups[groupIdx][0];
        var dots = colorGroups[groupIdx][1];

        this.grid.context.beginPath();
        var columns = Object.keys(dots);
        for (let i = 0; i < columns.length; i++) {
          let col = columns[i];
          let colDots = Object.values(dots[col]);
          for (let j = 0; j < colDots.length; j++) {
            let dot = colDots[j];

            this.updateDot(dot, color);
            this.grid.context.moveTo(dot.x, dot.y);
            if (dot.radius > 0) {
              this.grid.context.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
            }

            if (dot.animation.type !== "static") {
              animationIsActive = true;
            }
          }
        }
        this.grid.context.fillStyle = color;
        this.grid.context.fill();
      }

      if (animationIsActive || this.grid.drawMode.isDrawing) {
        window.requestAnimationFrame(this.drawAllDots);
      }
    },
    debounce(func, timeout = 200) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    resizeHandler(event) {
      console.log(event.target);
      if (event.target.document.body.clientWidth !== this.screenWidth) {
        this.init();
      }
    },
    updateDot(dot, color) {
      if (dot.animation.type == "static") {
        this.static(dot);
      } else if (dot.animation.type == "ripple") {
        this.ripple(dot);
      } else if (dot.animation.type == "popColor") {
        this.popColor(dot, color);
      }
    },
    static(dot) {
      if (dot.radius != this.grid.dotRadius) {
        dot.radius = this.grid.dotRadius;
      }
    },
    ripple(dot) {
      let RIPPLE_DURATION = this.grid.animations.ripple.duration; //Max time in ms for entire ripple effect to take place.
      let DOT_ANIMATION_DURATION = this.grid.animations.ripple.dotSpeed; //Time of dot animation in ms.

      //Get origin of animation
      let originX = this.grid.animations.ripple.origin.x;
      let originY = this.grid.animations.ripple.origin.y;

      //This ensures that all dots on screen when the animation starts will be finished after RIPPLE_DURATION seconds.
      //I want to rework this so the speed doesn't scale linearly, but logarithmically as distance from the origin increases.
      let visibleRippleDistance = Math.hypot(
        window.innerWidth / 2,
        window.innerHeight / 2
      );
      let rippleRatio = RIPPLE_DURATION / visibleRippleDistance;

      let delay = Math.hypot(originX - dot.x, originY - dot.y) * rippleRatio;

      //Compare current time to start time to calculate progress of animation
      let currentTime = Date.now();
      let elapsedTime = currentTime - dot.animation.startTime;

      if (elapsedTime < delay) {
        dot.radius = 0;
      } else if (elapsedTime > delay + DOT_ANIMATION_DURATION) {
        dot.radius = this.grid.dotRadius;
        if (elapsedTime >= DOT_ANIMATION_DURATION + RIPPLE_DURATION) {
          if (Math.random() > this.grid.colorRatio * 0.01) {
            dot.animation.type = "static";
          } else {
            //Sets the following animation to popColor. Delay is calculated here and set as startTime
            //for the next animation. This ensures continuity for the next animation.

            var dotCopy = JSON.parse(JSON.stringify(dot));

            dotCopy.animation.type = "popColor";
            dotCopy.animation.startTime =
              dot.animation.startTime +
              DOT_ANIMATION_DURATION +
              Math.max(delay, RIPPLE_DURATION) +
              Math.random() *
                (this.grid.animations.popColor.duration -
                  this.grid.animations.popColor.dotSpeed);

            let color = this.theme.colors[
              Math.floor(Math.random() * this.theme.colors.length)
            ];

            this.grid.allDots[color][dot.x] =
              this.grid.allDots[color][dot.x] || {};
            this.grid.allDots[color][dot.x][dot.y] = dotCopy;

            dot.animation.type = "static";
          }
        }
      } else {
        let animationTime = elapsedTime - delay;
        let growDistance = Math.abs(
          this.grid.maxDotRadius - this.grid.dotRadius
        );

        dot.radius =
          this.grid.dotRadius +
          ((DOT_ANIMATION_DURATION / 2 -
            Math.abs(animationTime - DOT_ANIMATION_DURATION / 2)) /
            (DOT_ANIMATION_DURATION / 2)) *
            growDistance;
      }
    },
    popColor(dot, color) {
      let DOT_ANIMATION_DURATION = this.grid.animations.popColor.dotSpeed;

      //Compare current time to start time to calculate progress of animation
      let currentTime = Date.now();
      let elapsedTime = currentTime - dot.animation.startTime;

      if (elapsedTime < 0) {
        dot.radius = 0;
      } else if (elapsedTime > DOT_ANIMATION_DURATION) {
        dot.radius = this.grid.dotRadius;
        dot.animation.type = "static";
      } else {
        let otherColors = this.theme.colors
          .concat(this.theme.gray)
          .filter((c) => c !== color);
        for (let i = 0; i < otherColors.length; i++) {
          try {
            delete this.grid.allDots[otherColors[i]][dot.x][dot.y];
          } catch (err) {
            //Do nothing. Most times the path to this dot won't exist, but the try catch is easier than checking for each key individually
          }
        }

        let animationTime = elapsedTime;
        let growDistance = Math.abs(
          this.grid.maxDotRadius - this.grid.dotRadius
        );

        dot.radius =
          this.grid.dotRadius +
          ((DOT_ANIMATION_DURATION / 2 -
            Math.abs(animationTime - DOT_ANIMATION_DURATION / 2)) /
            (DOT_ANIMATION_DURATION / 2)) *
            growDistance;
      }
    },
    toggleDrawMode() {
      this.grid.drawMode.active = !this.grid.drawMode.active;
      if (this.grid.drawMode.active) {
        document.addEventListener("mousedown", this.mouseDownHandler);
        document.addEventListener("mouseup", this.mouseUpHandler);
        document.addEventListener("touchstart", this.touchStartHandler);
        document.addEventListener("touchend", this.touchEndHandler);
        document.body.style.cursor = "pointer";
      } else {
        document.removeEventListener("mousedown", this.mouseDownHandler);
        document.removeEventListener("mouseup", this.mouseUpHandler);
        document.removeEventListener("touchstart", this.touchStartHandler);
        document.removeEventListener("touchend", this.touchEndHandler);
        document.body.style.cursor = "auto";
      }
    },
    roundClickToGrid(eventX, eventY) {
      let roundedX =
        Math.round(eventX / this.grid.dotSpacing) * this.grid.dotSpacing;
      let roundedY =
        Math.round(eventY / this.grid.dotSpacing) * this.grid.dotSpacing;

      if (
        Math.hypot(roundedX - eventX, roundedY - eventY) <
        this.grid.dotRadius + this.grid.dotSpacing / 2
      ) {
        return { x: roundedX, y: roundedY };
      } else {
        return null;
      }
    },
    addRoundedDot(eventX, eventY) {
      let rounded = this.roundClickToGrid(eventX, eventY);
      let start = Date.now();
      if (rounded !== null) {
        let dot = {
          x: rounded.x,
          y: rounded.y,
          radius: this.grid.dotRadius,
          animation: {
            type: "popColor",
            startTime: start,
          },
          drawn: true,
        };
        var color;
        if (this.grid.drawMode.color === "random") {
          color = this.theme.colors[
            Math.floor(Math.random() * this.theme.colors.length)
          ];
        } else {
          color = this.grid.drawMode.color;
        }
        this.grid.allDots[color][dot.x] = this.grid.allDots[color][dot.x] || {};
        this.grid.allDots[color][dot.x][dot.y] = dot;
      }
    },
    updateColorRatio(ratio) {
      this.grid.colorRatio = ratio;
      this.init();
    },
    mouseDownHandler(event) {
      var gridControls = document.getElementById("grid-controls");
      if (!gridControls.contains(event.target)) {
        this.grid.drawMode.isDrawing = true;
        this.addRoundedDot(event.pageX, event.pageY);
        this.drawAllDots();
        document.addEventListener("mousemove", this.mouseMoveHandler);
      }
    },
    mouseMoveHandler(event) {
      event.preventDefault();
      this.addRoundedDot(event.pageX, event.pageY);
    },
    mouseUpHandler() {
      this.grid.drawMode.isDrawing = false;
      document.removeEventListener("mousemove", this.mouseMoveHandler);
    },
    touchStartHandler(event) {
      var gridControls = document.getElementById("grid-controls");
      if (!gridControls.contains(event.target) && !this.grid.drawMode.isDrawing) {
        this.grid.drawMode.isDrawing = true;
        document.body.classList.add("overflow-hidden");
        let touch = event.touches[0];
        this.addRoundedDot(touch.pageX, touch.pageY);
        this.drawAllDots();
        document.addEventListener("touchmove", this.touchMoveHandler, {
          passive: false,
        });
      }
    },
    touchMoveHandler(event) {
      if (event.cancelable) {
        event.preventDefault();
      }
      let touch = event.touches[0];
      this.addRoundedDot(touch.pageX, touch.pageY);
    },
    touchEndHandler(event) {
      if (event.touches.length == 0) {
        this.grid.drawMode.isDrawing = false;
        document.body.classList.remove("overflow-hidden");
        document.removeEventListener("touchmove", this.touchMoveHandler, {
          passive: false,
        });
      }
    },
    log(ev) {
      console.log(ev);
    },
    // openRMRModal() {
    //   this.showRMRModal = true;
    //   document.body.classList.add("overflow-hidden");
    // },
    // closeRMRModal() {
    //   this.showRMRModal = false;
    //   document.body.classList.remove("overflow-hidden");
    // },
  },
};
</script>

<style scoped>
canvas.dots-canvas {
  background-color: #282c34;
  position: absolute;
  top: 0;
  left: 0;
}

.touch-action-none {
  touch-action: none;
  -ms-touch-action: none;
}
</style>
